import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import logger from 'utils/logger';

import i18n, { loadTranslations } from '../i18n';

export const useAsyncAction = (asyncAction, params = {}) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState('idle');
  const [asyncActionCalledCount, setAsyncActionCalledCount] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [args, setArgs] = useState(params.args ?? []);

  useEffect(() => {
    const runAsyncAction = async () => {
      setLoading(true);
      try {
        setStatus('loading');
        const response = await dispatch(asyncAction(...args));
        await loadTranslations(i18n, response?.settings?.language);
        setData(response);
        setStatus('success');
        setLoading(false);
      } catch (err) {
        logger.error('Failed to process async action: ');
        logger.error(err);

        setError(err);
        setStatus('failed');
        setLoading(false);
      }
    };

    if (params.deferred) {
      if (asyncActionCalledCount > 0) {
        runAsyncAction();
      }
    } else {
      runAsyncAction();
    }

    return () => {
      i18n.locale = 'en';
    };
  }, [args, asyncAction, asyncActionCalledCount, dispatch, params.deferred]);

  const handleDeferredAsyncActionRun = (...argments) => {
    setArgs(argments);
    setAsyncActionCalledCount((v) => v + 1);
  };

  return {
    loading,
    status,
    data,
    error,
    run: params.deferred ? handleDeferredAsyncActionRun : () => {},
  };
};

export const useDeferredAsyncAction = (asyncAction, params = {}) =>
  useAsyncAction(asyncAction, { deferred: true, ...params });
